/**
 * A maze that contains the Softmaze logo.
 *
 * The logo mark is one zig-zag stroke with a bar at each end. Laid on a
 * 13×13 grid it becomes a chain of cells; those are carved first, then a
 * recursive backtracker grows the rest of the maze outward from them, so
 * the result is still a perfect maze whose one true route *is* the mark.
 * `order` records when each cell was carved, which drives the fill-in
 * animation (glyph first, then the maze spreading from it).
 */
import { mulberry32 } from './maze'

export type Pt = [number, number] // [col, row]

/** Corner points of the main stroke, in 13×13 glyph coordinates. */
export const GLYPH_MAIN: Pt[] = [
  [12, 3],
  [8, 3],
  [8, 0],
  [0, 0],
  [0, 3],
  [4, 3],
  [4, 6],
  [8, 6],
  [8, 9],
  [12, 9],
  [12, 12],
  [4, 12],
  [4, 9],
  [0, 9],
]
/** The two end bars (vertical), each crossing the main stroke. */
export const GLYPH_BARS: [Pt, Pt][] = [
  [
    [12, 0],
    [12, 6],
  ],
  [
    [0, 6],
    [0, 12],
  ],
]
export const GLYPH_SIZE = 13

export interface LogoMaze {
  cols: number
  rows: number
  /** walls per cell: top, right, bottom, left */
  walls: [boolean, boolean, boolean, boolean][]
  /** carve order per cell (0 = first) */
  order: Int32Array
  /** glyph polylines as cell sequences, offset into the grid */
  strokes: Pt[][]
  /** one continuous cell sequence covering the whole glyph, for drawing */
  trace: Pt[]
  maxOrder: number
}

function expand(points: Pt[]): Pt[] {
  const out: Pt[] = []
  for (let i = 0; i < points.length; i++) {
    const [x, y] = points[i]
    if (i === 0) {
      out.push([x, y])
      continue
    }
    let [px, py] = out[out.length - 1]
    const dx = Math.sign(x - px)
    const dy = Math.sign(y - py)
    while (px !== x || py !== y) {
      px += dx
      py += dy
      out.push([px, py])
    }
  }
  return out
}

const DIRS = [
  [0, -1],
  [1, 0],
  [0, 1],
  [-1, 0],
] as const

export function generateLogoMaze(margin = 1, seed = 1): LogoMaze {
  const cols = GLYPH_SIZE + margin * 2
  const rows = cols
  const n = cols * rows
  const idx = (x: number, y: number) => y * cols + x
  const walls: LogoMaze['walls'] = Array.from({ length: n }, () => [true, true, true, true])
  const visited = new Uint8Array(n)
  const order = new Int32Array(n).fill(-1)
  let tick = 0

  const shift = (p: Pt): Pt => [p[0] + margin, p[1] + margin]
  // Carving uses the three geometric strokes; drawing uses one continuous
  // stroke that runs down the first bar, back to the junction, along the
  // main path, then up and down the second bar — so the trace is a single
  // unbroken line.
  const strokes: Pt[][] = [expand(GLYPH_MAIN).map(shift), ...GLYPH_BARS.map((b) => expand(b).map(shift))]

  // 1. Carve the glyph.
  const carve = (a: Pt, b: Pt) => {
    const d = DIRS.findIndex(([dx, dy]) => a[0] + dx === b[0] && a[1] + dy === b[1])
    if (d < 0) return
    walls[idx(a[0], a[1])][d] = false
    walls[idx(b[0], b[1])][(d + 2) % 4] = false
  }
  const glyphCells: number[] = []
  for (const stroke of strokes) {
    for (let i = 0; i < stroke.length; i++) {
      const c = idx(stroke[i][0], stroke[i][1])
      if (!visited[c]) {
        visited[c] = 1
        order[c] = tick++
        glyphCells.push(c)
      }
      if (i > 0) carve(stroke[i - 1], stroke[i])
    }
  }

  // 2. Grow the rest from the glyph outward (backtracker seeded with the glyph).
  const rnd = mulberry32(seed)
  const stack = [...glyphCells]
  for (let i = stack.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1))
    ;[stack[i], stack[j]] = [stack[j], stack[i]]
  }
  while (stack.length) {
    const cur = stack[stack.length - 1]
    const cx = cur % cols
    const cy = Math.floor(cur / cols)
    const options: number[] = []
    for (let d = 0; d < 4; d++) {
      const nx = cx + DIRS[d][0]
      const ny = cy + DIRS[d][1]
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue
      if (!visited[idx(nx, ny)]) options.push(d)
    }
    if (!options.length) {
      stack.pop()
      continue
    }
    const d = options[Math.floor(rnd() * options.length)]
    const nb = idx(cx + DIRS[d][0], cy + DIRS[d][1])
    walls[cur][d] = false
    walls[nb][(d + 2) % 4] = false
    visited[nb] = 1
    order[nb] = tick++
    stack.push(nb)
  }

  const [barA, barB] = GLYPH_BARS
  const trace = expand([barA[0], barA[1], ...GLYPH_MAIN, barB[0], barB[1]]).map(shift)

  return { cols, rows, walls, order, strokes, trace, maxOrder: tick - 1 }
}

/** Per-cell wall path data (right + bottom walls only; outer top/left drawn separately). */
export function cellWallPath(m: LogoMaze, cell: number, size: number): string {
  const x = (cell % m.cols) * size
  const y = Math.floor(cell / m.cols) * size
  const w = m.walls[cell]
  let d = ''
  if (w[1]) d += `M${x + size} ${y}v${size}`
  if (w[2]) d += `M${x} ${y + size}h${size}`
  return d
}

/** Path data for one glyph stroke through cell centres. */
export function strokePath(stroke: Pt[], size: number): string {
  const h = size / 2
  return stroke.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x * size + h} ${y * size + h}`).join('')
}
